import { DAI_MAT_KHAU_TOI_THIEU, NGUONG_NHAC_SINH_LAI, ROLE_LABEL } from '@qlk/shared';
import { KeyRound, Mail, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useToi } from '../../../lib/auth.js';
import { Nut, O, OThongTin, PageHeader, Skeleton, StatusBadge } from '../../../ui/index.js';
import {
  useDoiEmail,
  useDoiMatKhau,
  useSinhLaiMaDuPhong,
  useTat2fa,
  useTrangThai2fa,
} from '../api.js';

const DANG_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Nhắc một lần cho cả hai form: đổi xong là các thiết bị khác bị đăng xuất. */
const NHAC_PHIEN = 'Các thiết bị khác đang đăng nhập tài khoản này sẽ bị đăng xuất.';

export function TrangTaiKhoan() {
  const toi = useToi();
  const user = toi.data?.user;

  return (
    <>
      <PageHeader
        breadcrumb={['QUẢN TRỊ', 'Tài khoản']}
        title="Tài khoản của tôi"
        description="Đổi email đăng nhập và mật khẩu. Hai việc này chỉ áp dụng cho chính tài khoản bạn đang dùng."
      />

      {toi.isPending && <Skeleton className="h-40" />}

      {user && (
        <div className="grid grid-cols-1 gap-6 @min-[900px]/noi-dung:grid-cols-2">
          <section className="rounded-the border border-vien bg-be-mat p-6">
            <h2 className="text-tieu-de-hop font-bold text-chu">Thông tin đăng nhập</h2>
            <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <OThongTin nhan="Họ tên">{user.name}</OThongTin>
              <OThongTin nhan="Email đăng nhập">{user.email}</OThongTin>
              <OThongTin nhan="Vai trò">
                {user.roles.length > 0
                  ? user.roles.map((r) => ROLE_LABEL[r]).join(', ')
                  : 'Chưa gán vai trò'}
              </OThongTin>
              <OThongTin nhan="Xác thực hai bước">
                <StatusBadge tone={user.twoFactorEnabled ? 'ok' : 'warn'}>
                  {user.twoFactorEnabled ? 'Đã bật' : 'Chưa bật'}
                </StatusBadge>
              </OThongTin>
            </dl>
          </section>

          <Khoi2fa />
          <FormDoiMatKhau />
          <FormDoiEmail emailHienTai={user.email} />
        </div>
      )}
    </>
  );
}

function FormDoiMatKhau() {
  const doi = useDoiMatKhau();

  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setMoi] = useState('');
  const [confirmPassword, setXacNhan] = useState('');

  const quaNgan = newPassword.length > 0 && newPassword.length < DAI_MAT_KHAU_TOI_THIEU;
  const lechNhau = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const trungCu = newPassword.length > 0 && newPassword === currentPassword;

  const hopLe =
    currentPassword.length > 0 &&
    newPassword.length >= DAI_MAT_KHAU_TOI_THIEU &&
    confirmPassword === newPassword &&
    !trungCu;

  function luu() {
    doi.mutate(
      { currentPassword, newPassword, confirmPassword },
      {
        onSuccess: () => {
          setCurrent('');
          setMoi('');
          setXacNhan('');
        },
      },
    );
  }

  return (
    <section className="rounded-the border border-vien bg-be-mat p-6">
      <h2 className="flex items-center gap-2 text-tieu-de-hop font-bold text-chu">
        <KeyRound size={20} aria-hidden="true" />
        Đổi mật khẩu
      </h2>
      <p className="mt-1 text-phu text-chu-mo">
        Tối thiểu {DAI_MAT_KHAU_TOI_THIEU} ký tự. {NHAC_PHIEN}
      </p>

      <div className="mt-4 space-y-4">
        <O
          label="Mật khẩu hiện tại"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={setCurrent}
          required
        />
        <O
          label="Mật khẩu mới"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={setMoi}
          required
          moTa={
            quaNgan
              ? `Mật khẩu tối thiểu ${String(DAI_MAT_KHAU_TOI_THIEU)} ký tự`
              : trungCu
                ? 'Mật khẩu mới phải khác mật khẩu hiện tại'
                : undefined
          }
        />
        <O
          label="Nhập lại mật khẩu mới"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={setXacNhan}
          required
          moTa={lechNhau ? 'Hai lần nhập không khớp' : undefined}
        />
      </div>

      {doi.isError && (
        <p role="alert" className="mt-3 text-phu text-bad-chu">
          {doi.error.message}
        </p>
      )}
      {doi.isSuccess && (
        <p role="status" className="mt-3 text-phu text-ok-chu">
          Đã đổi mật khẩu. {NHAC_PHIEN}
        </p>
      )}

      <div className="mt-6 flex justify-end">
        <Nut kieu="chinh" disabled={!hopLe} dangChay={doi.isPending} onClick={luu}>
          Đổi mật khẩu
        </Nut>
      </div>
    </section>
  );
}

function FormDoiEmail({ emailHienTai }: { emailHienTai: string }) {
  const doi = useDoiEmail();

  const [currentPassword, setMatKhau] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setXacNhan] = useState('');

  const sach = email.trim().toLowerCase();
  const sachXacNhan = confirmEmail.trim().toLowerCase();

  const saiDang = sach.length > 0 && !DANG_EMAIL.test(sach);
  const lechNhau = sachXacNhan.length > 0 && sachXacNhan !== sach;
  const trungHienTai = sach.length > 0 && sach === emailHienTai.toLowerCase();

  const hopLe =
    currentPassword.length > 0 && DANG_EMAIL.test(sach) && sachXacNhan === sach && !trungHienTai;

  function luu() {
    doi.mutate(
      { currentPassword, email: sach, confirmEmail: sachXacNhan },
      {
        onSuccess: () => {
          setMatKhau('');
          setEmail('');
          setXacNhan('');
        },
      },
    );
  }

  return (
    <section className="rounded-the border border-vien bg-be-mat p-6">
      <h2 className="flex items-center gap-2 text-tieu-de-hop font-bold text-chu">
        <Mail size={20} aria-hidden="true" />
        Đổi email đăng nhập
      </h2>
      <p className="mt-1 text-phu text-chu-mo">
        Email mới trở thành tên đăng nhập ngay sau khi đổi. {NHAC_PHIEN}
      </p>

      <div className="mt-4 space-y-4">
        <O
          label="Email mới"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          required
          moTa={
            saiDang
              ? 'Email không hợp lệ'
              : trungHienTai
                ? 'Email mới trùng email hiện tại'
                : undefined
          }
        />
        <O
          label="Nhập lại email mới"
          type="email"
          autoComplete="email"
          value={confirmEmail}
          onChange={setXacNhan}
          required
          moTa={lechNhau ? 'Hai lần nhập email không khớp' : undefined}
        />
        <O
          label="Mật khẩu hiện tại"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={setMatKhau}
          required
          moTa="Nhập mật khẩu để xác nhận đây là bạn"
        />
      </div>

      {doi.isError && (
        <p role="alert" className="mt-3 text-phu text-bad-chu">
          {doi.error.message}
        </p>
      )}
      {doi.isSuccess && (
        <p role="status" className="mt-3 text-phu text-ok-chu">
          Đã đổi email đăng nhập. {NHAC_PHIEN}
        </p>
      )}

      <div className="mt-6 flex justify-end">
        <Nut kieu="chinh" disabled={!hopLe} dangChay={doi.isPending} onClick={luu}>
          Đổi email
        </Nut>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Xác thực hai bước                                                  */
/* ------------------------------------------------------------------ */

/**
 * Khối 2FA của trang Tài khoản.
 *
 * Chỉ **trạng thái và ba nút**; luồng bật nằm ở `/bat-2fa` (cùng màn mà
 * `CanDangNhap` dựng khi ép bật). Nhúng cả luồng bật vào đây là hai chỗ vẽ mã
 * QR, hai chỗ hiện mười mã dự phòng, và hai chỗ phải chặn đường ra.
 */
function Khoi2fa() {
  const tt = useTrangThai2fa();
  const sinhLai = useSinhLaiMaDuPhong();
  const tat = useTat2fa();

  const [matKhau, setMatKhau] = useState('');
  const [viec, setViec] = useState<'sinh-lai' | 'tat' | null>(null);

  const du = tt.data;
  const maMoi = sinhLai.data?.maDuPhong;

  function chay() {
    if (viec === 'sinh-lai') {
      sinhLai.mutate(
        { currentPassword: matKhau },
        {
          onSuccess: () => {
            setMatKhau('');
          },
        },
      );
    }
    if (viec === 'tat') {
      tat.mutate(
        { currentPassword: matKhau },
        {
          onSuccess: () => {
            setMatKhau('');
            setViec(null);
          },
        },
      );
    }
  }

  const loi = sinhLai.error ?? tat.error;

  return (
    <section data-khoi="2fa" className="rounded-the border border-vien bg-be-mat p-6">
      <h2 className="flex items-center gap-2 text-tieu-de-hop font-bold text-chu">
        <ShieldCheck size={20} aria-hidden="true" />
        Xác thực hai bước
      </h2>
      <p className="mt-1 text-phu text-chu-mo">
        Mỗi lượt đăng nhập cần thêm một mã 6 số từ app xác thực trên điện thoại.
      </p>

      {tt.isPending && <Skeleton className="mt-4 h-20" />}

      {du && (
        <>
          <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <OThongTin nhan="Trạng thái">
              <StatusBadge tone={du.daBat ? 'ok' : du.batBuoc ? 'bad' : 'warn'}>
                {du.daBat ? 'Đã bật' : 'Chưa bật'}
              </StatusBadge>
            </OThongTin>
            <OThongTin nhan="Vai trò của bạn">
              {du.batBuoc ? 'Bắt buộc bật' : 'Không bắt buộc, nên bật'}
            </OThongTin>
            {du.daBat && (
              <OThongTin nhan="Mã dự phòng còn lại">
                <span className={du.maDuPhongConLai <= NGUONG_NHAC_SINH_LAI ? 'text-warn-chu' : ''}>
                  {String(du.maDuPhongConLai)} mã
                </span>
              </OThongTin>
            )}
          </dl>

          {!du.daBat && (
            <Link
              to="/bat-2fa"
              className="vung-bam mt-4 inline-flex items-center rounded-o bg-nhan px-4 text-co-ban font-medium text-white"
            >
              Bật xác thực hai bước
            </Link>
          )}

          {du.daBat && (
            <>
              {du.maDuPhongConLai <= NGUONG_NHAC_SINH_LAI && (
                <p className="mt-4 rounded-[10px] bg-warn-nen px-3 py-2 text-phu text-warn-chu">
                  Gần cạn mã dự phòng. Sinh bộ mới trước khi hết — hết mã mà mất điện thoại thì phải
                  nhờ quản trị khác gỡ hộ.
                </p>
              )}

              {/*
                Mười mã mới hiện **ngay tại đây** sau khi sinh lại, một lần duy
                nhất. Không điều hướng sang màn khác: đổi trang giữa lúc mã đang
                hiện là cách chắc nhất để mất chúng.
              */}
              {maMoi && (
                <div data-khoi="ma-du-phong-moi" className="mt-4">
                  <p className="text-co-ban font-medium text-chu">
                    Bộ mã mới — lưu lại, bộ cũ đã hết giá trị:
                  </p>
                  <ul className="mt-2 grid grid-cols-2 gap-2 rounded-the border border-vien bg-nen p-4 sm:grid-cols-5">
                    {maMoi.map((ma) => (
                      <li
                        key={ma}
                        className="font-mono text-co-ban tracking-wider text-chu select-all"
                      >
                        {ma}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {viec === null ? (
                <div className="mt-4 flex flex-wrap gap-3">
                  <Nut
                    onClick={() => {
                      setViec('sinh-lai');
                    }}
                  >
                    Sinh lại mã dự phòng
                  </Nut>
                  {/*
                    Người thuộc nhóm bắt buộc không có nút tắt. Máy chủ cũng từ
                    chối (403) — đây chỉ là lớp thứ hai cho đỡ bấm vào một thứ
                    không làm được.
                  */}
                  {!du.batBuoc && (
                    <Nut
                      onClick={() => {
                        setViec('tat');
                      }}
                    >
                      Tắt xác thực hai bước
                    </Nut>
                  )}
                </div>
              ) : (
                <div className="mt-4 max-w-[320px]">
                  <O
                    label="Mật khẩu hiện tại"
                    type="password"
                    autoComplete="current-password"
                    value={matKhau}
                    onChange={setMatKhau}
                    required
                    moTa={
                      viec === 'tat'
                        ? 'Tắt xong, đăng nhập chỉ còn cần mật khẩu.'
                        : 'Bộ mã cũ sẽ hết giá trị ngay.'
                    }
                  />
                  <div className="mt-3 flex gap-3">
                    <Nut
                      kieu="chinh"
                      disabled={matKhau.length === 0}
                      dangChay={sinhLai.isPending || tat.isPending}
                      onClick={chay}
                    >
                      {viec === 'tat' ? 'Tắt' : 'Sinh bộ mã mới'}
                    </Nut>
                    <Nut
                      onClick={() => {
                        setViec(null);
                        setMatKhau('');
                      }}
                    >
                      Thôi
                    </Nut>
                  </div>
                </div>
              )}
            </>
          )}

          {loi && (
            <p role="alert" className="mt-3 text-phu text-bad-chu">
              {loi.message}
            </p>
          )}
        </>
      )}
    </section>
  );
}
